import React from "react";
import { Clock, Coins, Users } from "lucide-react";

const PROCESS_IMG =
    "https://static.prod-images.emergentagent.com/jobs/81057d82-68a6-4ab2-8cb6-656337b4e5ea/images/2425dfe26c1e1cf2c0635b92c3eed20e40a4d5a1745b1b208ac4ac049f76e69c.png";

const items = [
    {
        icon: Clock,
        n: "01",
        title: "Time",
        body: "Spot issues earlier, avoid rework, and move from idea to approval faster.",
    },
    {
        icon: Coins,
        n: "02",
        title: "Money",
        body: "Make confident decisions before materials, build hours or supplier time are committed.",
    },
    {
        icon: Users,
        n: "03",
        title: "Understanding",
        body: "Give clients, agencies, suppliers and production teams a shared view of the space.",
    },
];

export default function WhyItMatters() {
    return (
        <section
            id="why-it-matters"
            data-testid="why-it-matters-section"
            className="section-pad relative bg-navy-deep text-background"
        >
            <div className="container-edge">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
                    <div className="md:col-span-5">
                        <h2
                            data-testid="why-it-matters-heading"
                            className="font-display text-4xl md:text-5xl lg:text-[56px] tracking-tight font-medium leading-[1.04] text-balance"
                        >
                            Before an event is built, it has to be{" "}
                            <span className="italic font-normal text-champagne">
                                understood.
                            </span>
                        </h2>
                    </div>
                    <div className="md:col-span-7 md:pl-10 text-background/80 text-[15px] leading-relaxed space-y-5">
                        <p>
                            Events are complex physical environments. They involve
                            space, movement, scale, branding, atmosphere, guest
                            experience, venue restrictions, install windows, supplier
                            input and budget decisions.
                        </p>
                        <p>
                            A strong idea can quickly become difficult to explain
                            through moodboards, reference images and floor plans
                            alone. 3D visualisation gives everyone involved a shared
                            view of what is being created before time, money and
                            materials are committed.
                        </p>
                    </div>
                </div>

                <div className="mt-20 grid grid-cols-1 md:grid-cols-3 border border-background/15">
                    {items.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <div
                                key={it.n}
                                className={`p-8 md:p-10 lg:p-12 ${
                                    i !== items.length - 1
                                        ? "md:border-r border-background/15"
                                        : ""
                                } ${i !== items.length - 1 ? "border-b md:border-b-0 border-background/15" : ""}`}
                            >
                                <div className="flex items-start justify-between">
                                    <Icon
                                        size={26}
                                        strokeWidth={1.25}
                                        className="text-background/80"
                                    />
                                    <span className="text-[11px] tracking-[0.2em] text-background/40">
                                        {it.n}
                                    </span>
                                </div>
                                <h3 className="mt-10 font-display text-2xl md:text-3xl font-medium tracking-tight">
                                    {it.title}
                                </h3>
                                <p className="mt-4 text-background/70 text-[14px] leading-relaxed">
                                    {it.body}
                                </p>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-20 relative aspect-[21/9] overflow-hidden">
                    <img
                        src={PROCESS_IMG}
                        alt="CAD process wireframe to render"
                        className="w-full h-full object-cover opacity-90"
                    />
                    <div className="absolute inset-0 bg-foreground/30" />
                    <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                        <p className="overline !text-background/70">
                            Wireframe → Layout → Visual → Delivery
                        </p>
                        <p className="text-background/70 text-xs hidden md:block">
                            A shared view, from rough idea to client-ready output.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
